import React, { useState, useEffect, useCallback } from 'react';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './PageCommon.css';
import './Tokens.css';
import { formatMoney2, parseStrikeAmount } from '../utils/planPrice';

function PlanPriceBlock({ price, priceStrike, lang }) {
  const main = formatMoney2(price);
  const strikeNum = parseStrikeAmount(priceStrike);
  return (
    <div className="tokens-plan-card__price-block">
      {strikeNum != null && (
        <div className="plan-price-strike">${formatMoney2(strikeNum)}</div>
      )}
      <div className="tokens-plan-card__price">
        ${main}
        <span>/{lang === 'en' ? 'mo' : 'mes'}</span>
      </div>
    </div>
  );
}

function parsePlan(raw) {
  try {
    return typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {};
  } catch {
    return {};
  }
}

function formatExpire(planExpire, lang) {
  if (planExpire == null || planExpire === '') return null;
  const n = Number(planExpire);
  if (Number.isNaN(n)) return null;
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'es', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function Tokens() {
  const { lang } = useLang();
  const { user, fetchUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [dash, setDash] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchPlans = useCallback(() => {
    return api
      .get('/rt/plans')
      .then((r) => {
        if (r.data?.success) {
          const list = r.data.data || [];
          setPlans([...list].sort((a, b) => Number(a.price) - Number(b.price)));
        }
      })
      .catch(() => {});
  }, []);

  const fetchDash = useCallback(() => {
    return api
      .get('/user/get_dashboard')
      .then((r) => {
        if (r.data?.success) setDash(r.data.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchDash();
  }, [fetchPlans, fetchDash]);

  const currentPlan = parsePlan(user?.plan);
  const used = dash?.aiTokens?.used ?? 0;
  const limit = dash?.aiTokens?.limit ?? 0;
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const warn80 = limit > 0 && pct >= 80 && pct < 100;
  const exhausted = limit > 0 && pct >= 100;

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const r = await api.post('/rt/billing-portal');
      const url = r.data?.url;
      if (r.data?.success && url) {
        window.location.href = url;
      } else {
        alert(r.data?.msg || (lang === 'en' ? 'Could not open billing portal.' : 'No se pudo abrir el portal de facturación.'));
      }
    } catch {
      alert(lang === 'en' ? 'Connection error' : 'Error de conexión');
    }
    setBillingLoading(false);
  };

  const handleCancelSubscription = async () => {
    const ok = window.confirm(
      lang === 'en'
        ? 'Cancel subscription at the end of the current billing period?'
        : '¿Cancelar la suscripción al final del periodo de facturación actual?',
    );
    if (!ok) return;
    setCancelLoading(true);
    try {
      const r = await api.post('/rt/cancel-subscription');
      if (r.data?.success) {
        alert(r.data?.msg || (lang === 'en' ? 'Subscription updated.' : 'Suscripción actualizada.'));
        await fetchUser();
      } else {
        alert(r.data?.msg || (lang === 'en' ? 'Could not cancel.' : 'No se pudo cancelar.'));
      }
    } catch {
      alert(lang === 'en' ? 'Connection error' : 'Error de conexión');
    }
    setCancelLoading(false);
  };

  const handleUpgrade = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const r = await api.post('/user/create_stripe_session', { planId });
      const url = r.data?.session?.url || r.data?.url;
      if (r.data?.success && url) {
        window.location.href = url;
      } else {
        const msg =
          r.data?.msg ||
          (lang === 'en' ? 'Could not start checkout.' : 'No se pudo iniciar el pago.');
        alert(msg);
      }
    } catch {
      alert(lang === 'en' ? 'Connection error' : 'Error de conexión');
    }
    setCheckoutLoading(null);
  };

  const expireStr = formatExpire(user?.plan_expire, lang);

  return (
    <div className="page tokens-page">
      <div className="tokens-page__header">
        <h1>{lang === 'en' ? 'Plans' : 'Planes'}</h1>
        <p>
          {lang === 'en'
            ? 'Your subscription, AI token allowance, and upgrades'
            : 'Tu suscripción, cupo de tokens IA y mejoras'}
        </p>
      </div>

      {warn80 && (
        <div className="tokens-page__alert tokens-page__alert--warn">
          {lang === 'en'
            ? 'Your tokens are running low. Upgrade your plan soon to avoid interruptions.'
            : 'Tus tokens se están agotando. Mejora tu plan pronto para evitar cortes.'}
        </div>
      )}
      {exhausted && (
        <div className="tokens-page__alert tokens-page__alert--crit">
          {lang === 'en'
            ? 'Your tokens are exhausted. AI will not reply until you upgrade or purchase more tokens.'
            : 'Tus tokens se han agotado. Tu IA no responderá hasta que mejores tu plan o compres más tokens.'}
        </div>
      )}

      <div className="tokens-usage">
        <div className="tokens-usage__head">
          <span>{lang === 'en' ? 'AI token usage (billing period)' : 'Uso de tokens IA (periodo)'}</span>
          <span className="tokens-usage__nums">
            {`${used.toLocaleString()} / ${limit > 0 ? limit.toLocaleString() : '—'}`}
          </span>
        </div>
        <div className="tokens-usage__bar">
          <div
            className={`tokens-usage__fill${pct >= 80 ? ' tokens-usage__fill--high' : ''}`}
            style={{ width: `${limit > 0 ? pct : 0}%` }}
          />
        </div>
      </div>

      <div className="tokens-current">
        <div>
          <div className="tokens-current__title">
            {lang === 'en' ? 'Current plan' : 'Plan actual'}
          </div>
          <div className="tokens-current__name">{currentPlan?.title || '—'}</div>
          <div className="tokens-current__meta">
            {parseStrikeAmount(currentPlan?.price_strike) != null && (
              <div className="plan-price-strike">
                ${formatMoney2(parseStrikeAmount(currentPlan?.price_strike))}
              </div>
            )}
            <div className="tokens-current__price-line">
              ${formatMoney2(currentPlan?.price ?? 0)}/
              {lang === 'en' ? 'mo' : 'mes'}
              {expireStr
                ? ` · ${lang === 'en' ? 'Renews / expires' : 'Renueva / vence'}: ${expireStr}`
                : ''}
            </div>
          </div>
        </div>
        <span className="tokens-current__badge">
          {lang === 'en' ? 'Current plan' : 'Plan actual'}
        </span>
      </div>

      {user?.subscription_status === 'active' && (
        <div className="tokens-sub-actions">
          <button
            type="button"
            className="tokens-sub-actions__btn tokens-sub-actions__btn--primary"
            disabled={billingLoading}
            onClick={handleManageBilling}
          >
            {billingLoading ? '...' : lang === 'en' ? 'Manage Billing' : 'Gestionar facturación'}
          </button>
          <button
            type="button"
            className="tokens-sub-actions__btn tokens-sub-actions__btn--cancel"
            disabled={cancelLoading}
            onClick={handleCancelSubscription}
          >
            {cancelLoading ? '...' : lang === 'en' ? 'Cancel Subscription' : 'Cancelar suscripción'}
          </button>
        </div>
      )}
      {user?.subscription_status === 'cancelling' && (
        <p className="tokens-sub-notice">
          {lang === 'en'
            ? 'Your subscription will end at the end of the billing period.'
            : 'Tu suscripción finalizará al terminar el periodo de facturación.'}
        </p>
      )}

      {plans.length > 0 && (
        <>
          <h2 className="tokens-section__title">
            {lang === 'en' ? 'Available plans' : 'Planes disponibles'}
          </h2>
          <div className="tokens-plans-grid">
            {plans.map((plan) => {
              const isCurrent = Number(currentPlan?.id) === Number(plan.id);
              const aiRaw = plan.ai_tokens;
              const aiTok = Number(aiRaw) > 0 ? Number(aiRaw) : 0;
              const approxMsg = aiTok > 0 ? Math.floor(aiTok / 400) : 0;
              const storage = plan.storage != null && plan.storage !== '' ? String(plan.storage) : null;
              return (
                <div
                  key={plan.id}
                  className={`tokens-plan-card${isCurrent ? ' tokens-plan-card--current' : ''}`}
                >
                  {isCurrent && (
                    <span className="tokens-plan-card__badge">
                      {lang === 'en' ? 'Current' : 'Actual'}
                    </span>
                  )}
                  <h3>{plan.title}</h3>
                  <PlanPriceBlock price={plan.price} priceStrike={plan.price_strike} lang={lang} />
                  {plan.short_description && (
                    <p className="tokens-plan-card__desc">{plan.short_description}</p>
                  )}
                  <ul>
                    <li>
                      {lang === 'en' ? 'WhatsApp profiles:' : 'Perfiles WhatsApp:'}{' '}
                      {plan.qr_account ?? 0}
                    </li>
                    <li>
                      {lang === 'en' ? 'AI tokens / month:' : 'Tokens IA / mes:'}{' '}
                      <>
                        {aiTok.toLocaleString()} (~{approxMsg}{' '}
                        {lang === 'en' ? 'msgs' : 'mensajes'})
                      </>
                    </li>
                    <li>
                      {lang === 'en' ? 'Contacts:' : 'Contactos:'} {plan.contact_limit ?? '—'}
                    </li>
                    <li>
                      {lang === 'en' ? 'Chatbot:' : 'Chatbot:'}{' '}
                      {plan.allow_chatbot
                        ? lang === 'en'
                          ? 'Included'
                          : 'Incluido'
                        : lang === 'en'
                          ? 'Not included'
                          : 'No incluido'}
                    </li>
                    {storage && (
                      <li>
                        {lang === 'en' ? 'Storage:' : 'Almacenamiento:'} {storage}
                      </li>
                    )}
                    <li>
                      {lang === 'en' ? 'Duration:' : 'Duración:'}{' '}
                      {plan.plan_duration_in_days ?? '—'}{' '}
                      {lang === 'en' ? 'days' : 'días'}
                    </li>
                  </ul>
                  {isCurrent ? (
                    <button type="button" className="tokens-plan-card__btn tokens-plan-card__btn--current" disabled>
                      {lang === 'en' ? 'Current plan' : 'Plan actual'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="tokens-plan-card__btn tokens-plan-card__btn--upgrade"
                      disabled={checkoutLoading === plan.id}
                      onClick={() => handleUpgrade(plan.id)}
                    >
                      {checkoutLoading === plan.id
                        ? '...'
                        : lang === 'en'
                          ? 'Upgrade'
                          : 'Mejorar'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
